/**
 * The choice a person gets when a workspace already exists for their work email.
 *
 * Maria, 4 Sep 2026: *"they get their own org if one doesn't already exist … or they get a
 * choice if one does based on their work domain (excluding gmail, hotmail, etc)"*.
 *
 * 🔴 THIS COMPONENT IS THE SAFETY PROPERTY, NOT A NICETY. Everything upstream of it — the
 * verified-address gates in `ensure-self-serve-org.ts`, Clerk's refusal to let anybody
 * verify @gmail.com — narrows WHICH workspaces may be shown. None of it decides that
 * somebody joins one. A human clicking a button decides that, here, and nowhere else.
 * Putting the wrong person into a workspace hands them another company's connections and
 * credentials, so no OFFER is ever taken automatically.
 *
 * ⚠️ 5 Oct 2026 (Maria): a VERIFIED company address whose domain a workspace already holds is
 * now joined to it automatically, server-side (`src/onboarding/workspace-by-domain.ts`). That
 * path never produces an offer and never reaches this component; offers shown here (manual
 * invitations, suggestion-mode domains) are still a human's choice.
 *
 * ⚠️ Rendering this and having the person do nothing is CORRECT. They have no workspace
 * while they think about it, which is the same state they were in a second earlier. Making
 * one for them "so the page works" is the defect.
 */

import { Building2, Clock } from "lucide-react";
import { useState } from "react";
import { useOrgContext } from "../providers/OrgProvider";

export function OrgJoinChoice() {
  const { orgJoinOffers, orgJoinRequests, acceptOrgJoinOffer, createOwnWorkspace } =
    useOrgContext();
  const [busy, setBusy] = useState<string | null>(null);

  if (orgJoinOffers.length === 0 && orgJoinRequests.length === 0) {
    return null;
  }

  const run = (key: string, action: () => Promise<void>) => () => {
    setBusy(key);
    void action().finally(() => setBusy(null));
  };

  return (
    <div className="border-b border-blue-500/30 bg-blue-500/10 px-6 py-3 text-sm text-blue-900 dark:text-blue-200">
      <div className="flex items-start gap-2">
        <Building2 className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1 space-y-3">
          {orgJoinOffers.length > 0 && (
            <div className="space-y-2">
              <p className="font-medium">
                {orgJoinOffers.length === 1
                  ? "Your team already has a workspace here."
                  : "Your team already has workspaces here."}
              </p>
              <p>
                Join one and you share their connections and data. Start your own and you get a
                private workspace instead. Nothing happens until you pick.
              </p>
              <ul className="space-y-2">
                {orgJoinOffers.map((offer) => (
                  <li key={offer.id} className="flex flex-wrap items-center gap-2">
                    <span data-testid="org-join-offer-name" className="font-medium">
                      {offer.orgName || "A team workspace"}
                    </span>
                    {offer.kind === "suggestion" && (
                      <span className="text-xs opacity-80">
                        someone there has to approve you first
                      </span>
                    )}
                    <button
                      type="button"
                      disabled={busy !== null}
                      onClick={run(offer.id, () => acceptOrgJoinOffer(offer.id))}
                      className="inline-flex shrink-0 items-center rounded-md border px-2 py-1 text-xs font-medium hover:bg-background/70 disabled:opacity-60"
                    >
                      {offer.kind === "invitation" ? "Join" : "Ask to join"}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {orgJoinRequests.length > 0 && (
            <div className="space-y-1">
              {orgJoinRequests.map((request) => (
                <p key={request.id} className="flex items-center gap-2">
                  <Clock className="h-4 w-4 shrink-0" />
                  <span>
                    You asked to join{" "}
                    <span className="font-medium">{request.orgName || "a team workspace"}</span>.
                    Someone there needs to approve it before you can get in.
                  </span>
                </p>
              ))}
            </div>
          )}

          <button
            type="button"
            disabled={busy !== null}
            onClick={run("own", createOwnWorkspace)}
            className="inline-flex shrink-0 items-center rounded-md border px-2 py-1 text-xs font-medium hover:bg-background/70 disabled:opacity-60"
          >
            Create my own workspace
          </button>
        </div>
      </div>
    </div>
  );
}
